import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ColumnDef } from '@tanstack/react-table';
import { Boxes, PackagePlus, Pencil, Trash2 } from 'lucide-react';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { Button } from '@/shared/ui/buttons/Button';
import { IconButton } from '@/shared/ui/buttons/IconButton';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { DataTable } from '@/shared/ui/data-table/DataTable';
import { Pagination } from '@/shared/ui/data-table/Pagination';
import { FilterBar } from '@/shared/ui/data-table/FilterBar';
import { extractErrorMessage } from '@/shared/api/errors';
import { usePackagesList } from '@/entities/packages';
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue';
import { usePerm } from '@/shared/hooks/usePerm';
import { FeaturesPackagesDeleteDialog } from '@/features/packages';
import { formatDate } from '@/shared/utils/date';
import { formatDecimalTrim } from '@/shared/utils/currency';
import type { ApiPackagesGetItem } from '@/shared/types/api/packages';

const PAGE_SIZE = 20;

const formatMoney = (v: number | string | null | undefined): string => {
  if (v === null || v === undefined || v === '') return '—';
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return '—';
  return formatDecimalTrim(n);
};

export const WidgetsPackagesMain = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const canCreate = usePerm('packages.create');
  const canEdit = usePerm('packages.edit');
  const canDelete = usePerm('packages.delete');

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 350);

  const [deleteTarget, setDeleteTarget] = useState<ApiPackagesGetItem | null>(null);

  const params = useMemo(
    () => ({
      page,
      page_size: PAGE_SIZE,
      search: debouncedSearch.trim() || undefined,
    }),
    [page, debouncedSearch],
  );

  const query = usePackagesList(params);

  const columns = useMemo<ColumnDef<ApiPackagesGetItem>[]>(
    () => [
      {
        accessorKey: 'name',
        header: t('packages.list.columns.name'),
        cell: (info) => (
          <span className="block max-w-[260px] truncate font-medium text-ink">
            {info.row.original.name}
          </span>
        ),
      },
      {
        accessorKey: 'price',
        header: t('packages.list.columns.price'),
        cell: (info) => formatMoney(info.row.original.price),
      },
      {
        accessorKey: 'weight',
        header: t('packages.list.columns.weight'),
        cell: (info) => {
          const row = info.row.original;
          const w = formatMoney(row.weight);
          if (w === '—') return '—';
          return `${w} ${row.weight_unit}`;
        },
      },
      {
        accessorKey: 'parcels_count',
        header: t('packages.list.columns.parcelsCount'),
        cell: (info) => (
          <span className="font-medium">{info.row.original.parcels_count}</span>
        ),
      },
      {
        accessorKey: 'created_at',
        header: t('packages.list.columns.createdAt'),
        cell: (info) => formatDate(info.row.original.created_at),
      },
      {
        id: 'actions',
        header: t('packages.list.columns.actions'),
        enableSorting: false,
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="flex items-center gap-1.5">
              {canEdit && (
                <IconButton
                  label={t('common.edit')}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/packages/${row.id}/edit`);
                  }}
                >
                  <Pencil className="h-4 w-4" />
                </IconButton>
              )}
              {canDelete && (
                <IconButton
                  label={t('common.delete')}
                  variant="danger"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteTarget(row);
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </IconButton>
              )}
            </div>
          );
        },
      },
    ],
    [navigate, t, canEdit, canDelete],
  );

  return (
    <>
      <PageHeader
        title={t('packages.list.title')}
        subtitle={t('packages.list.subtitle')}
        right={
          canCreate ? (
            <Button
              onClick={() => navigate('/packages/new')}
              leftIcon={<PackagePlus className="h-4 w-4" />}
              title={t('packages.list.newPackage')}
              aria-label={t('packages.list.newPackage')}
            >
              <span className="hidden sm:inline">{t('packages.list.newPackage')}</span>
            </Button>
          ) : null
        }
      />
      <div className="space-y-4 px-4 py-4 lg:px-6 lg:py-6">
        <FilterBar
          searchValue={search}
          onSearchChange={(v) => {
            setPage(1);
            setSearch(v);
          }}
          searchPlaceholder={t('packages.list.searchPlaceholder')}
        />

        {query.error ? (
          <ErrorState
            message={extractErrorMessage(query.error, t('packages.list.loadError'))}
            onRetry={() => void query.refetch()}
          />
        ) : (
          <>
            <DataTable
              data={query.data?.results ?? []}
              columns={columns}
              isLoading={query.isLoading}
              emptyTitle={t('packages.list.emptyTitle')}
              emptyDescription={t('packages.list.emptyDescription')}
              emptyIcon={<Boxes className="h-8 w-8" aria-hidden />}
              getRowId={(row) => row.id}
              numbered
              pageIndex={page - 1}
              pageSize={PAGE_SIZE}
            />
            <Pagination
              page={page}
              pageSize={PAGE_SIZE}
              total={query.data?.count ?? 0}
              onPageChange={setPage}
            />
          </>
        )}
      </div>

      <FeaturesPackagesDeleteDialog
        pkg={deleteTarget}
        onClose={() => setDeleteTarget(null)}
      />
    </>
  );
};
